import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useFavorites } from '../context/FavoritesContext';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { MapPin, Wifi, Wind, Car, Users, Star, Eye, Heart } from 'lucide-react';

const ListingCard = ({ listing }) => {
  const { t } = useLanguage();
  const { isFavorite, toggleFavorite, loading } = useFavorites();

  const typeLabels = {
    apartment: t('apartment'),
    room: t('room'),
    hotel: t('hotel'),
    studio: t('studio'),
  };

  const defaultImage = "https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=800";
  const favorite = isFavorite(listing.listing_id);

  const handleFavoriteClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggleFavorite(listing.listing_id);
  };

  return (
    <Link to={`/listings/${listing.listing_id}`} data-testid={`listing-card-${listing.listing_id}`}>
      <Card className="group overflow-hidden border border-border/50 hover:border-primary/30 hover:shadow-hover transition-all duration-300 hover:-translate-y-1">
        <div className="relative aspect-[4/3] overflow-hidden">
          <img
            src={listing.images?.[0] || defaultImage}
            alt={listing.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            onError={(e) => { e.target.src = defaultImage; }}
          />
          <div className="absolute top-3 left-3 flex gap-2">
            <Badge variant="secondary" className="bg-white/90 backdrop-blur-sm">
              {typeLabels[listing.listing_type] || listing.listing_type}
            </Badge>
            {listing.is_featured && (
              <Badge className="bg-accent text-accent-foreground">
                <Star className="h-3 w-3 mr-1" />
                Featured
              </Badge>
            )}
          </div>
          <div className="absolute top-3 right-3 flex gap-2">
            {listing.is_verified && (
              <Badge className="bg-primary text-primary-foreground">Vérifié</Badge>
            )}
            <Button
              variant="ghost"
              size="icon"
              className={`h-8 w-8 rounded-full bg-white/90 backdrop-blur-sm hover:bg-white ${favorite ? 'text-red-500' : 'text-muted-foreground'}`}
              onClick={handleFavoriteClick}
              disabled={loading}
              data-testid={`favorite-btn-${listing.listing_id}`}
            >
              <Heart className={`h-4 w-4 ${favorite ? 'fill-current' : ''}`} />
            </Button>
          </div>
        </div>
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-2 mb-2">
            <h3 className="font-heading font-semibold text-lg line-clamp-1 group-hover:text-primary transition-colors">
              {listing.title}
            </h3>
            <div className="flex items-center gap-1 text-muted-foreground text-sm shrink-0">
              <Eye className="h-4 w-4" />
              {listing.views || 0}
            </div>
          </div>

          <div className="flex items-center gap-1 text-muted-foreground text-sm mb-3">
            <MapPin className="h-4 w-4 shrink-0" />
            <span className="line-clamp-1">{listing.neighborhood}, {listing.city}</span>
          </div>

          <div className="flex items-center gap-3 mb-4 text-muted-foreground">
            {listing.has_wifi && <Wifi className="h-4 w-4" title={t('wifi')} />}
            {listing.has_ac && <Wind className="h-4 w-4" title={t('ac')} />}
            {listing.has_parking && <Car className="h-4 w-4" title={t('parking')} />}
            <div className="flex items-center gap-1 ml-auto">
              <Users className="h-4 w-4" />
              <span className="text-sm">{listing.max_guests}</span>
            </div>
          </div>

          <div className="flex items-end justify-between">
            <div>
              <span className="text-2xl font-bold text-primary">
                {listing.price_per_night?.toLocaleString()}
              </span>
              <span className="text-muted-foreground text-sm ml-1">FCFA{t('perNight')}</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

export default ListingCard;
